/**
 * 《一封家书》— the letter text from story.txt, split into beats (owner: prologue-builder).
 *
 * Every character is verbatim from story.txt except the documented normalisations in
 * docs/STORY_ADAPTATION.md (上传→上船, 腊→蜡, 三重的的→三重的). `joinLetter()` must reproduce the story
 * letter exactly (unit test in prologue.test.ts). Beats are grouped into sections, one per story flag
 * the prologue owns; a section reveals its `before` beats, optionally runs a memory stage, then reveals
 * its `after` beats, optionally plays a cinematic and advances the flag.
 */
import type { StoryFlagId } from '@/story/flags';

/** Memory stage keys — the same ids the cinematics module uses for MEMORY_STAGE_STYLES. */
export const MEMORY_STAGE_IDS = ['memory-storm', 'memory-island', 'memory-black-water', 'memory-farewell', 'memory-sword-training'] as const;
export type MemoryStageId = (typeof MEMORY_STAGE_IDS)[number];

export interface LetterBeat {
  id: string;
  text: string;
  /** A physical paragraph of the letter ends after this beat (joined with a newline). */
  paragraphEnd?: boolean;
}

export const LETTER_BEATS = [
  { id: 'salute', text: '亲爱的琼：', paragraphEnd: true },
  { id: 'open', text: '我已经远在南陆了。上次给你写信的时候是一个月前吧，我们踏上了去南陆的船。是你在祝福我吗？' },
  { id: 'storm-lead', text: '我们的船在一个月的时间中只遇上一次大的风浪。' },
  { id: 'storm-after', text: '天啊，你要看见这样大的风浪一定为吓坏的，黑夜中闪电和雨水混在一起。不过我是一个勇敢的男人，在颠簸的甲板上我仍然稳稳的站着，看着水手们控制着帆。' },
  { id: 'island-lead', text: '天亮的时候雨停了，风浪也停了，我们发现到了一个不认识的小岛。' },
  { id: 'island-after', text: '船长说他从来不知道这里──也许是他的生意没有做到这里吧？这家伙一路上在推销着他的不知道从哪里运来的香料。他还没有来过我们的长阳城────我也没有去过呢，我们在跟他说起的时候他很羡慕的样子：“真的吗？你们那里晚上整个城都用蜡烛？这得花多少钱啊？”我们跟他说蜡烛很便宜的时候，他显然陶醉在以后做生意的憧憬中。唉，不说这个了。船长也不清楚到了哪里，于是我们都下船了，我看到船长很尊敬的请一个穿得破破烂烂的人出来，那个人站在小岛的沙滩的地方，用木桩摆了一个五角形状，然后将他们之间两两连接，嘴里不知道念叨些什么，过一会儿那些线有一根就亮了起来。“朝西南方，”那个穿得很破烂的人显得很疲惫。船长很尊敬的说：“感谢你，卢宝丁巫师。”然后号召大家上船，往西南方行动。', paragraphEnd: true },
  { id: 'water-lead', text: '我觉得这一幕好熟悉啊。还记得我们在星塘的日子吗？' },
  { id: 'water-after', text: '那天我病了────不知道什么病，吃了那些草药也没有用。后来他们请来村里的万千梅婆婆来，她把你们全都赶出去了，然后关上门对着我念了一句听不懂的话，在地上画了一个三角形的法阵。忽然间三角形的上方出现了一个黑色的三角形。她让我盯着看，问我看见什么了。我那时候头痛得厉害，但仍然盯着看，刚开始什么也没有看见，后来看见好多水，好多好多黑色的水，不断向外涌出。我不敢看了，闭上眼睛跟婆婆说：我看见好多水，好多黑色的水。婆婆半天没有吭声，我睁眼看她，她眉头紧缩。你知道的，婆婆平时是很慈祥的，很少见她有特殊的情绪，村子里所有的人看见她都很平静，总是拄着说不清颜色的棍子去井边打水，然后拧着回家。我问婆婆怎么了，婆婆只是深深的看着我，说：“江子潮，你很快就会没事。但是，需要一点点药材。你去南陆吧，那里有你的答案。”', paragraphEnd: true },
  { id: 'farewell-lead', text: '后来她把我赶出去，把父母叫来说了一会儿话。' },
  { id: 'farewell-father', text: '说完后，父亲说：“婆婆说你的病需要新鲜的南陆旱龟蛋。家里，长阳城都不会有你想要的东西，你去南陆吧。你也大了，自己出去闯闯。家里还需要我来照顾。我听说长阳城经验最丰富的长者赵呈博也要去南陆，你跟他一起去吧！”', paragraphEnd: true },
  { id: 'farewell-after', text: '我本来准备到长阳城去找赵呈博的，第二天他居然自己跑来找我了。唉，看来我的病真的是很紧急了。我们就一起上路了。琼，刚开始你还哭呢。不过我开始也挺兴奋，后来就觉得想你了。我想如果不是我生这场病的话，我们应该可以成亲，生孩子，种地啦，就像隔壁的二哥二嫂一样。你织布的手艺所有人都羡慕呢，以后别傻傻的把布送给赵三了，他根本不用，只是高价卖给过路的商人，让他们拿到长阳城卖给那些贵妇人。我打猎的手艺也不错，旁边的那座山，我是村子里第一个能爬到三重的。我还想着爬到四重五重呢，胆小的赵三害怕，硬拖着我回来。', paragraphEnd: true },
  { id: 'road-lead', text: '我跟赵呈博长者一路上他跟我讲了很多东西，比如长阳城的繁华，比如见到皇上应该如何行礼。我才知道现在已经是1157年了。' },
  { id: 'road-after', text: '一路上赵长者真的就像老师一样教导着我，甚至教我剑术。', paragraphEnd: true },
  { id: 'road-ship', text: '船一路西南方向行驶，天气越来越热。离开的时候星塘还在结冰呢，这里却越来越热。长者说离太阳越来越近。' },
  { id: 'arrive', text: '我们最后终于到了南陆了。我在码头给你写下这封信，用蜡封好，放到一个玻璃瓶里，交给随船返回的人，希望你能收到这封信。以后我在南陆寻找旱龟蛋的时候有机会我给你写信。等我回来，敲锣打鼓来娶你！', paragraphEnd: true },
  { id: 'sign', text: '子潮。' },
] as const satisfies readonly LetterBeat[];

export type LetterBeatId = (typeof LETTER_BEATS)[number]['id'];

export interface LetterSection {
  /** Flag advanced when this section finishes. */
  flag: StoryFlagId;
  /** Beats revealed before the memory / cinematic. */
  before: readonly LetterBeatId[];
  /** Memory stage launched between `before` and `after`. */
  memory?: MemoryStageId;
  /** Beats revealed after the memory returns (instantly when the memory was skipped). */
  after: readonly LetterBeatId[];
  /** Skippable timeline played after the beats (sealing the letter, the prophecy). */
  cinematic?: string;
}

/** Story flags owned by the prologue, in canonical order. */
export const PROLOGUE_FLAGS = [
  'LETTER_OPEN',
  'MEMORY_STORM',
  'MEMORY_ISLAND_RITUAL',
  'MEMORY_BLACK_WATER',
  'MEMORY_FAREWELL',
  'MEMORY_SWORD_TRAINING',
  'LETTER_SENT',
  'PROPHECY_REVEAL',
] as const satisfies readonly StoryFlagId[];
export type PrologueFlag = (typeof PROLOGUE_FLAGS)[number];

export const LETTER_SECTIONS: readonly LetterSection[] = [
  { flag: 'LETTER_OPEN', before: ['salute', 'open'], after: [] },
  { flag: 'MEMORY_STORM', before: ['storm-lead'], memory: 'memory-storm', after: ['storm-after'] },
  { flag: 'MEMORY_ISLAND_RITUAL', before: ['island-lead'], memory: 'memory-island', after: ['island-after'] },
  { flag: 'MEMORY_BLACK_WATER', before: ['water-lead'], memory: 'memory-black-water', after: ['water-after'] },
  { flag: 'MEMORY_FAREWELL', before: ['farewell-lead'], memory: 'memory-farewell', after: ['farewell-father', 'farewell-after'] },
  { flag: 'MEMORY_SWORD_TRAINING', before: ['road-lead'], memory: 'memory-sword-training', after: ['road-after', 'road-ship'] },
  { flag: 'LETTER_SENT', before: ['arrive', 'sign'], after: [], cinematic: 'prologue-letter-sent' },
  { flag: 'PROPHECY_REVEAL', before: [], after: [], cinematic: 'prologue-prophecy' },
];

/** Typo normalisations applied to story.txt before comparing (docs/STORY_ADAPTATION.md). */
export const STORY_NORMALISATIONS: readonly (readonly [string, string])[] = [
  ['号召大家上传', '号召大家上船'],
  ['用腊封好', '用蜡封好'],
  ['万千梅的点播', '万千梅的点拨'],
  ['爬到三重的的', '爬到三重的'],
];

export function normaliseStoryText(text: string): string {
  let out = text;
  for (const [from, to] of STORY_NORMALISATIONS) out = out.split(from).join(to);
  return out;
}

export function letterBeat(id: string): LetterBeat {
  const b = LETTER_BEATS.find((x) => x.id === id);
  if (!b) throw new Error(`unknown letter beat ${id}`);
  return b;
}

export function isLetterBeatId(id: string): id is LetterBeatId {
  return LETTER_BEATS.some((b) => b.id === id);
}

/** Reassembles the letter: beats concatenate, paragraph ends become newlines. */
export function joinLetter(beats: readonly LetterBeat[] = LETTER_BEATS): string {
  let out = '';
  beats.forEach((b, i) => {
    out += b.text;
    if (b.paragraphEnd && i < beats.length - 1) out += '\n';
  });
  return out;
}

export function sectionFor(flag: StoryFlagId): LetterSection | undefined {
  return LETTER_SECTIONS.find((s) => s.flag === flag);
}

/** Every beat id in reveal order across all sections. */
export function sectionBeatOrder(): LetterBeatId[] {
  return LETTER_SECTIONS.flatMap((s) => [...s.before, ...s.after]);
}

/**
 * Beats that are already written when the game resumes at `flag` (that flag is set): all beats of
 * sections up to and including it. [] for TITLE / null; the whole letter for flags past the prologue.
 */
export function beatsWrittenAt(flag: StoryFlagId | null): LetterBeat[] {
  if (!flag || flag === 'TITLE') return [];
  const idx = LETTER_SECTIONS.findIndex((s) => s.flag === flag);
  if (idx < 0) return LETTER_BEATS.map((b) => b);
  return LETTER_SECTIONS.slice(0, idx + 1).flatMap((s) => [...s.before, ...s.after]).map(letterBeat);
}

/** Sections still to play after resuming at `flag` (the flag itself is already set). */
export function sectionsAfter(flag: StoryFlagId | null): LetterSection[] {
  if (!flag || flag === 'TITLE') return [...LETTER_SECTIONS];
  const idx = LETTER_SECTIONS.findIndex((s) => s.flag === flag);
  if (idx < 0) return [];
  return LETTER_SECTIONS.slice(idx + 1);
}

/** Sentence-final punctuation after which a display chunk may end. */
const CHUNK_BREAKS = new Set(['。', '！', '？', '；']);
const CHUNK_TRAILERS = new Set(['”', '’', '」', '』', ')', '）']);

/**
 * Splits a beat into paragraphs for the letter panel (which fits ~6 lines per page). Chunks only end
 * after sentence punctuation (and any closing quote), never mid-sentence; joining them reproduces the
 * beat exactly. Short beats stay whole.
 */
export function splitForDisplay(text: string, maxChars = 96, minChars = 36): string[] {
  if (text.length <= maxChars) return [text];
  const sentences: string[] = [];
  let cur = '';
  for (let i = 0; i < text.length; i++) {
    const ch = text[i] as string;
    cur += ch;
    if (CHUNK_BREAKS.has(ch)) {
      while (i + 1 < text.length && CHUNK_TRAILERS.has(text[i + 1] as string)) { i++; cur += text[i] as string; }
      sentences.push(cur);
      cur = '';
    }
  }
  if (cur) sentences.push(cur);
  const chunks: string[] = [];
  let acc = '';
  for (const s of sentences) {
    if (acc.length >= minChars && acc.length + s.length > maxChars) { chunks.push(acc); acc = ''; }
    acc += s;
  }
  if (acc) {
    const last = chunks[chunks.length - 1];
    if (acc.length < minChars && last !== undefined && last.length + acc.length <= maxChars * 1.25) chunks[chunks.length - 1] = last + acc;
    else chunks.push(acc);
  }
  return chunks;
}
